// src/utils/eapProgram.js
//
// Shape helpers for organizations/{id}.eapProgram. The block grew from
// "phone + link" into the provider's whole front door (per-service intakes,
// access codes, alternate channels), so parsing lives here rather than being
// duplicated between the super-admin editor and every member-facing surface.
//
// Everything here is backwards compatible: programs saved before these fields
// existed still resolve, and `services` accepts both the legacy array of plain
// strings and the current array of objects.

/**
 * Services as {label, url, hint}. Accepts:
 *   - legacy: ['Counselling', 'Legal advice']
 *   - current: [{label, url, hint}]
 * A service with no url still renders — it just isn't a link.
 */
export function normalizeServices(services) {
    if (!Array.isArray(services)) return [];
    return services
        .map((s) => {
            if (typeof s === 'string') return { label: s.trim(), url: '', hint: '' };
            if (s && typeof s === 'object') {
                return {
                    label: (s.label || '').trim(),
                    url: (s.url || '').trim(),
                    hint: (s.hint || '').trim()
                };
            }
            return null;
        })
        .filter((s) => s && s.label);
}

// Admin input: one service per line, "Label | url | hint after sign-in".
// A textarea beats a drag-and-drop repeater here — these are pasted straight
// off a provider's one-pager, usually all at once.
export function parseServicesInput(text) {
    return String(text || '')
        .split('\n')
        .map((line) => {
            const [label, url, hint] = line.split('|').map((p) => (p || '').trim());
            return { label: label || '', url: url || '', hint: hint || '' };
        })
        .filter((s) => s.label);
}

export function serializeServicesInput(services) {
    return normalizeServices(services)
        .map((s) => [s.label, s.url, s.hint].filter(Boolean).join(' | '))
        .join('\n');
}

/**
 * Append the provider's own attribution parameters to an outbound link, so
 * usage on their side can be traced back to this platform (which is also the
 * evidence a broker asks for). `linkParams` is a raw query string the provider
 * supplies — we never invent tracking they haven't asked for.
 *
 * Preserves any query string already on the url, and returns the url untouched
 * if it can't be parsed, because a broken support link is far worse than a
 * missing UTM tag.
 */
export function withAttribution(url, linkParams) {
    if (!url) return url;
    const extra = String(linkParams || '').trim().replace(/^[?&]+/, '');
    if (!extra) return url;
    try {
        const parsed = new URL(url);
        new URLSearchParams(extra).forEach((value, key) => {
            parsed.searchParams.set(key, value);
        });
        return parsed.toString();
    } catch (err) {
        return url;
    }
}

// "24/7" was hardcoded next to every configured phone before availability was a
// field, which asserted a service level for providers that run business-hours
// intake. Absent an explicit value we now say nothing.
export function availabilityLabel(eap) {
    return (eap?.availability || '').trim();
}
